// =============================================================================
// components/community/OverflowMenu.tsx — the "…" overflow bottom sheet used by
// the community interior screens. Renders ONLY the actions it is given — every
// visible row is wired to a real behavior by the caller (NO dead menu items:
// e.g. Report/Copy-link are omitted when no backend/clipboard support exists).
// =============================================================================
import React from 'react';
import { Modal, View, Text, TouchableOpacity, Pressable, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons, IoniconName } from '../ui/Icon';
import { GlassSheet } from '../glass';
import { colors, spacing, radius, fonts } from '../../constants/theme';

export interface OverflowAction {
  key: string;
  label: string;
  icon: IoniconName;
  destructive?: boolean;
  onPress: () => void;
}

export default function OverflowMenu({
  visible,
  onClose,
  title,
  actions,
}: {
  visible: boolean;
  onClose: () => void;
  title?: string;
  actions: OverflowAction[];
}) {
  const insets = useSafeAreaInsets();
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose} accessibilityLabel="Close menu" />
      <View style={[styles.sheetWrap, { paddingBottom: Math.max(insets.bottom, spacing.lg) }]}>
        <GlassSheet>
          {!!title && <Text style={styles.title} numberOfLines={1}>{title}</Text>}
          {actions.map((a) => (
            <TouchableOpacity
              key={a.key}
              style={styles.row}
              activeOpacity={0.7}
              onPress={() => { onClose(); a.onPress(); }}
              accessibilityRole="button"
              accessibilityLabel={a.label}
            >
              <View style={[styles.iconWrap, a.destructive && styles.iconWrapDestructive]}>
                <Ionicons name={a.icon} size={17} color={a.destructive ? colors.error : colors.primary} />
              </View>
              <Text style={[styles.label, a.destructive && styles.labelDestructive]}>{a.label}</Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity
            style={styles.cancel}
            onPress={onClose}
            activeOpacity={0.8}
            accessibilityRole="button"
            accessibilityLabel="Cancel"
          >
            <Text style={styles.cancelText}>Cancel</Text>
          </TouchableOpacity>
        </GlassSheet>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(26,26,46,0.35)' },
  sheetWrap: { paddingHorizontal: spacing.md },
  title: {
    ...fonts.bold, fontSize: 16, color: colors.ink,
    textAlign: 'center', marginBottom: spacing.md,
  },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.md,
    paddingVertical: spacing.md,
  },
  iconWrap: {
    width: 34, height: 34, borderRadius: 11,
    backgroundColor: colors.primary + '15',
    alignItems: 'center', justifyContent: 'center',
  },
  iconWrapDestructive: { backgroundColor: colors.error + '15' },
  label: { ...fonts.semibold, fontSize: 15, color: colors.ink },
  labelDestructive: { color: colors.error },
  cancel: {
    marginTop: spacing.sm, borderRadius: radius.full,
    paddingVertical: spacing.md, alignItems: 'center',
    backgroundColor: 'rgba(108,92,231,0.10)',
  },
  cancelText: { ...fonts.semibold, fontSize: 15, color: colors.primary },
});
